import { Mark } from '@tiptap/core'
import { ReactMarkViewRenderer } from '@tiptap/react'
import {
  MANUAL_INSTRUCTION_PLACEHOLDER,
  MANUAL_INSTRUCTION_REGEX,
} from '@artem-lex/compliance-documents'
import { ManualInstructionMarkView } from './ManualInstructionMarkView'

export {
  MANUAL_INSTRUCTION_PLACEHOLDER,
  MANUAL_INSTRUCTION_REGEX,
}

export const MANUAL_INSTRUCTION_MARK = 'manualInstruction'

export interface ManualInstructionMatch {
  index: number
  length: number
  text: string
  /** Text inside the brackets after `MANUAL_INSTRUCTION:`; null for bare `[MANUAL_INSTRUCTION]`. */
  instruction: string | null
}

/**
 * Finds every manual-instruction placeholder in `text` and its optional guidance.
 * Uses the shared global regex: each `exec` returns the next match until none remain (`null`).
 */
export function findManualInstructionMatches(
  text: string
): ManualInstructionMatch[] {
  const matches: ManualInstructionMatch[] = []
  MANUAL_INSTRUCTION_REGEX.lastIndex = 0
  let nextMatch = MANUAL_INSTRUCTION_REGEX.exec(text)
  while (nextMatch !== null) {
    const fullMatch = nextMatch[0]
    const rawInstruction = nextMatch[1] ?? null
    const instruction =
      rawInstruction != null && rawInstruction.trim() !== ''
        ? rawInstruction.trim()
        : null
    matches.push({
      index: nextMatch.index,
      length: fullMatch.length,
      text: fullMatch,
      instruction,
    })
    nextMatch = MANUAL_INSTRUCTION_REGEX.exec(text)
  }
  return matches
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    manualInstruction: {
      setManualInstruction: (attributes?: {
        instruction?: string | null
      }) => ReturnType
      unsetManualInstruction: () => ReturnType
    }
  }
}

export const ManualInstructionMark = Mark.create({
  name: MANUAL_INSTRUCTION_MARK,
  /**
   * Default inclusive marks extend onto text typed at the inner edge after the marked
   * range is deleted; that made the manual-instruction UI reappear on the next keystroke.
   */
  inclusive: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addMarkView() {
    return ReactMarkViewRenderer(ManualInstructionMarkView, {
      as: 'div',
      className: 'block w-full',
    })
  },

  addAttributes() {
    return {
      instruction: {
        default: null,
        parseHTML: (element) =>
          (element as HTMLElement).getAttribute('data-instruction') ?? null,
        renderHTML: (attributes) => {
          if (!attributes.instruction) {
            return {}
          }
          return { 'data-instruction': attributes.instruction }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-manual-instruction]',
        getAttrs: (element) => {
          const el = element as HTMLElement
          return {
            instruction: el.getAttribute('data-instruction') ?? null,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        class: 'manual-instruction',
        'data-manual-instruction': '',
        ...this.options.HTMLAttributes,
        ...HTMLAttributes,
      },
      0,
    ]
  },

  renderMarkdown: (node, _helpers) => {
    const instruction = node.attrs?.instruction as string | null
    if (instruction != null && instruction.trim() !== '') {
      return `[MANUAL_INSTRUCTION: ${instruction}]`
    }
    return MANUAL_INSTRUCTION_PLACEHOLDER
  },
})
